import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, PenLine } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "./common";

const Stars = ({ value, onChange, size = "w-5 h-5", testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n)}
        className={onChange ? "hover:scale-110 transition-transform" : "cursor-default"}
        aria-label={`${n} stars`}>
        <Star className={`${size} ${n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
      </button>
    ))}
  </span>
);

/* Phase 4: structured reviews on global airport/airline pages. One review per user. */
export default function EntityReviews({ type, code, name }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [ratings, setRatings] = useState({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/v1/${type}s/${encodeURIComponent(code)}/reviews`)
    .then((r) => setData(r.data)).catch(() => setData({ summary: { count: 0 }, reviews: [], categories: {} }));
  useEffect(() => { setData(null); setEditing(false); load(); }, [type, code, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Spinner />;
  const { summary, categories, reviews, my_review: mine } = data;

  const startEdit = () => {
    setRatings(mine?.ratings || {});
    setText(mine?.text || "");
    setEditing(true);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!Object.keys(ratings).length) { toast.error("Rate at least one category"); return; }
    setBusy(true);
    api.post(`/v1/${type}s/${encodeURIComponent(code)}/reviews`, { ratings, text })
      .then(() => { toast.success("Review saved"); setEditing(false); load(); })
      .catch((err) => toast.error(err.response?.data?.detail || "Could not save review"))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    api.delete(`/v1/${type}s/${encodeURIComponent(code)}/reviews`)
      .then(() => { toast.success("Review deleted"); setEditing(false); load(); })
      .catch(() => toast.error("Could not delete review"));
  };

  return (
    <div className="space-y-8" data-testid="entity-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading font-900 text-blue-600 text-xl sm:text-2xl flex items-center gap-2">
            <Star className="w-6 h-6" /> Traveler reviews
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{summary.count ? `${summary.count} review${summary.count > 1 ? "s" : ""} of ${name}` : `Be the first to review ${name}.`}</p>
        </div>
        {summary.overall != null && (
          <div className="text-right" data-testid="reviews-overall">
            <span className="font-heading font-900 text-3xl text-amber-500 notranslate">{summary.overall}</span>
            <span className="text-sm text-zinc-400"> / 5</span>
            <div className="mt-0.5"><Stars value={Math.round(summary.overall)} size="w-4 h-4" /></div>
          </div>
        )}
      </div>

      {summary.count > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2" data-testid="reviews-categories">
          {Object.entries(summary.by_category || {}).filter(([, v]) => v.count).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-zinc-600">{v.label}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="w-24 h-1.5 bg-zinc-200 rounded-full overflow-hidden"><span className="block h-full bg-amber-400 rounded-full" style={{ width: `${(v.avg / 5) * 100}%` }} /></span>
                <span className="font-700 text-zinc-700 w-7 text-right notranslate">{v.avg}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {!editing && (
        user ? (
          <button onClick={startEdit} data-testid="review-write-btn"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-800 px-4 py-2.5 rounded-lg transition-colors">
            <PenLine className="w-4 h-4" /> {mine ? "Edit your review" : "Write a review"}
          </button>
        ) : (
          <p className="text-sm text-zinc-500" data-testid="review-signin-prompt">
            <Link to="/login" className="text-blue-700 font-800 hover:underline">Sign in</Link> to share your experience at {name}.
          </p>
        )
      )}

      {editing && (
        <form onSubmit={submit} className="border border-zinc-300 bg-white p-5 space-y-4" data-testid="review-form">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {Object.entries(categories).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="text-sm text-zinc-700">{label}</span>
                <Stars value={ratings[k]} onChange={(n) => setRatings((r) => ({ ...r, [k]: n }))} testid={`review-stars-${k}`} />
              </div>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000}
            placeholder={`Tell other travelers about ${name}… (optional)`} data-testid="review-text"
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600" />
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" disabled={busy} data-testid="review-submit"
              className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-sm font-800 px-4 py-2 rounded-lg">
              {busy ? "Saving…" : "Save review"}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="text-sm font-700 text-zinc-500 hover:text-zinc-800">Cancel</button>
            {mine && (
              <button type="button" onClick={remove} data-testid="review-delete"
                className="ml-auto inline-flex items-center gap-1 text-sm font-700 text-rose-600 hover:text-rose-800">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="reviews-list">
        {reviews.map((r) => (
          <div key={r.review_id} className="border border-zinc-200 bg-white rounded-xl p-4" data-testid={`review-${r.review_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-800 text-sm text-zinc-800">{r.user_name}</span>
              <Stars value={Math.round(r.overall)} size="w-3.5 h-3.5" />
              <span className="text-xs font-700 text-amber-600 notranslate">{r.overall}</span>
              <span className="text-[11px] text-zinc-400 ml-auto">{(r.updated_at || r.created_at || "").slice(0, 10)}</span>
            </div>
            {r.text && <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{r.text}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
